import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError } from '@shared/api/client';
import { useSessionStore } from '@shared/auth/session';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { ErrorBanner } from '@shared/ui/ErrorBanner';

import { editorCopy } from '../_shared/copy';
import { editorialErrors } from '../_shared/errors';
import { useCreatePost } from '../_shared/mutations';
import { PostEditor } from '../_shared/PostEditor';
import { useRelatedCandidates, useTags } from '../_shared/queries';
import { copy } from './copy';

/**
 * Author a new post — the same canvas and rail as the edit route, empty and
 * fixed at draft.
 *
 * **It is a route rather than a mode of the edit page**, and six differences
 * follow from that rather than from taste: the trail names the route because
 * there is no post to name; the title carries `autofocus` because it is the
 * first thing written; the flow opens on one empty paragraph so the caret has
 * somewhere to land; the state is a badge with no control because draft is the
 * only state a new post can hold; there is no «ارفع للمراجعة», because nobody
 * hands over text typed in the same sitting in front of a blank page and
 * submitting before saving sends what its writer has not re-read; and there is
 * no delete, because there is nothing yet to delete.
 *
 * **No publication-date field, here or on the edit route.** The date is written
 * by the approval — a writer cannot publish, so a date they typed is one they
 * cannot honour.
 */
export function EditorialNewPage() {
  const navigate = useNavigate();
  const profile = useSessionStore((state) => state.profile);

  const tags = useTags();
  const [relatedTerm, setRelatedTerm] = useState('');
  const related = useRelatedCandidates(relatedTerm, true);
  const createPost = useCreatePost();

  const [writeError, setWriteError] = useState<unknown>(null);
  const [announcement, setAnnouncement] = useState('');

  /**
   * The byline, read off the session because that is where the service reads it.
   *
   * There is no account register call behind this any more and no picker in
   * front of it: `POST /admin/editorialPosts` takes the author from the token
   * and forbids an `authorId` on the body outright.
   */
  const authorName = profile?.displayName ?? '';

  return (
    <>
      <Breadcrumb
        items={[
          { label: copy.crumbs.root, to: '/' },
          { label: copy.crumbs.list, to: '/editorial' },
          { label: copy.crumbs.current },
        ]}
      />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <PostEditor
        state="DRAFT"
        stateHint={copy.stateHint}
        publishNotice={copy.publishNotice}
        initialTitle=""
        initialBody={[]}
        authorName={authorName}
        tags={tags.data?.items ?? []}
        initialTagIds={[]}
        relatedCandidates={related.data?.items ?? []}
        onRelatedSearch={setRelatedTerm}
        initialRelatedIds={[]}
        relatedLabels={{}}
        busy={createPost.isPending}
        primaryLabel={editorCopy.actions.saveDraft}
        onSave={(draft) => {
          setWriteError(null);
          createPost.mutate(draft, {
            onSuccess: (post) => {
              setAnnouncement(copy.announce(post.title));
              // The writer stays with the post they just started rather than
              // being returned to a list they did not ask for.
              void navigate(`/editorial/${post.id}/edit`);
            },
            onError: setWriteError,
          });
        }}
        banner={writeError ? <ErrorBanner {...resolve(writeError)} /> : undefined}
      />
    </>
  );
}

function resolve(error: unknown) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, editorialErrors);

  return {
    message: resolved.message,
    tone: resolved.tone === 'danger' ? ('danger' as const) : ('neutral' as const),
  };
}
